import React, { useEffect, useState } from 'react'
import { Button, Grid, Image, Segment } from 'semantic-ui-react'
import select_icon from '../../../../assets/svg/modal_select_icon.svg'
import unselect_icon from '../../../../assets/svg/modal_unselected_icon.svg'
import WizardComponent from '../../../../components/Modal/WizardComponent'
import { IUserDetail } from '../../../../types/user'
import { checkRole } from '../../../../utils/checkRole'
import { IWizardProject } from '../../types/wizardData'

interface Props {
  open: boolean
  data: IWizardProject
  wizardFinishedSteps: number[]
  user: IUserDetail | null
  setWizardFinishedSteps: (steps: number[]) => void
  setWizardStep: (step: string) => void
  setProjectWizardData: (data: IWizardProject) => void
  onClose: () => void
}

const billingProcedureTypes = [
  { id: 1, rights: [9, 10], type: 'scheduled_payment', name: 'Scheduled Payments', description: 'Create payment schedules including single milestone payments with target timelines and invoice status. Maintain milestones by editing postponed dates and marking milestones as paid.' },
  { id: 2, rights: [9, 10], type: 'billing_by_tasks', name: 'Billing by Tasks', description: 'Prepare outgoing invoices with positions billed by performed units. Maintain an overview of offered, used and remaining units within the budget.' },
  { id: 3, rights: [9, 10], type: 'billing_on_actual', name: 'Billing on Actual', description: 'Generate outgoing invoices with positions billed on actual performed work. Billable amounts are based on individual hourly rates of the individual employee groups and departments.' }
]

const WizardSelectBillingProcedure = (props: Props): JSX.Element => {
  const {
    open,
    data,
    wizardFinishedSteps,
    user,
    setWizardFinishedSteps,
    setWizardStep,
    setProjectWizardData,
    onClose,
  } = props
  const [billingProcedure, setBillingProcedure] = useState<string>(data.billing_procedure ?? '')

  useEffect(() => {
    if (wizardFinishedSteps.includes(6)) {
      if (billingProcedure === '') {
        setWizardFinishedSteps([1, 2, 3, 4, 5])
      }
    }
  }, [setWizardFinishedSteps, wizardFinishedSteps, billingProcedure])

  const onClick = (type: string) => {
    setBillingProcedure(type)
  }

  const renderContent = () => {
    return (
      <Grid>
        <br />
        <div>
          <h5>
            Billing for Outgoing Invoices
          </h5>
          {billingProcedureTypes.map(bp => {
            if (user && checkRole(user, bp.rights)) {
              return (
                <Grid.Row key={bp.id}>
                  <Segment
                    onClick={() => onClick(bp.type)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: '4.8px',
                      height: '6.6rem',
                      marginTop: '1rem',
                      opacity: bp.type === billingProcedure || billingProcedure === '' ? 1 : .6,
                      border: `2.5px solid ${bp.type === billingProcedure ? '#08D6A1' : '#BEC8C3'}`,
                      width: '100%'
                    }}>
                    <b>{bp.name}</b>
                    <Image style={{ float: 'right', marginTop: '1.4rem' }} src={bp.type === billingProcedure ? select_icon : unselect_icon} />
                    <p style={{ fontSize: '12px', marginTop: '0.2rem', letterSpacing: '.25px', color: '#232C58' }}>{bp.description}</p>
                  </Segment>
                </Grid.Row>
              )
            }
            return null
          })}
        </div>
      </Grid>
    )
  }

  const renderFooter = () => {
    return (
      <div>
        <Button
          inverted
          color='grey'
          size='small'
          onClick={goToPreviousStep}
          content='Back'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0, color: '#747678', borderColor: '#BEC8C3', marginRight: '1rem' }}
        />
        <Button
          primary={true}
          disabled={billingProcedure === ''}
          size='small'
          onClick={goToNextStep}
          content='Next'
          style={{ width: '8.57rem', height: '2.14rem', borderRadius: '.34rem', padding: 0 }}
        />
      </div>
    )
  }

  const goToPreviousStep = () => {
    const _data: IWizardProject = {
      ...data,
      billing_procedure: billingProcedure
    }
    setWizardStep('step-four')
    setProjectWizardData(_data)
  }

  const goToNextStep = () => {
    const _data: IWizardProject = {
      ...data,
      billing_procedure: billingProcedure
    }
    if (!wizardFinishedSteps.includes(6)) {
      setWizardFinishedSteps([1, 2, 3, 4, 5, 6])
    }
    setWizardStep('step-six')
    setProjectWizardData(_data)
  }

  const renderWizard = () => {
    const _data: IWizardProject = {
      ...data,
      billing_procedure: billingProcedure,
      health: 'on_track'
    }
    const _steps = [
      { key: 1, name: 'step-one' },
      { key: 2, name: 'step-two' },
      { key: 3, name: 'step-three' },
      { key: 4, name: 'step-four' },
      { key: 5, name: 'step-five' },
      { key: 6, name: 'step-six' }
    ]
    return (
      <WizardComponent
        open={open}
        close={onClose}
        content={renderContent()}
        headerContent='Select Billing Procedure'
        headerColor='#232C58'
        footer={renderFooter()}
        steps={6}
        activeStep={5}
        width={'756px'}
        finishedSteps={wizardFinishedSteps}
        setWizardStep={setWizardStep}
        setProjectWizardData={setProjectWizardData}
        data={_data}
        stepStringList={_steps}
      />
    )
  }

  return renderWizard()
}

export default WizardSelectBillingProcedure
